<template>
    <div class="onboarding-tour" v-if="showTour">
        <!-- Welcome Modal -->
        <welcome-tour-modal 
            v-model="showWelcomeModal"
            :user-name="userName"
            @start-tour="startTour"
            @skip-tour="skipTour"
        />

        <!-- Tour Overlay -->
        <div v-if="tourStarted" class="tour-overlay" @click.self="handleOverlayClick">
            <!-- Highlight Box -->
            <div 
                v-if="currentHighlight"
                class="tour-highlight"
                :style="highlightStyle"
            ></div>

            <!-- Tooltip -->
            <div 
                v-if="currentStep"
                class="tour-tooltip"
                :class="[`tooltip-${tooltipPosition}`]"
                :style="tooltipStyle"
            >
                <div class="tooltip-arrow" :class="[`arrow-${tooltipPosition}`]"></div>
                
                <div class="tooltip-header">
                    <span class="step-indicator">{{ currentStepIndex + 1 }} / {{ steps.length }}</span>
                    <button class="close-btn" @click="endTour">
                        <v-icon size="18" color="grey">mdi-close</v-icon>
                    </button>
                </div>

                <div class="tooltip-content">
                    <h4 class="tooltip-title">{{ currentStep.title }}</h4>
                    <p class="tooltip-description">{{ currentStep.description }}</p>
                </div>

                <div class="tooltip-actions">
                    <v-btn 
                        v-if="currentStepIndex > 0"
                        text 
                        small 
                        color="grey darken-1"
                        @click="prevStep"
                    >
                        Back
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn 
                        v-if="currentStepIndex < steps.length - 1"
                        color="primary" 
                        depressed 
                        small
                        @click="nextStep"
                    >
                        Next
                        <v-icon right small>mdi-arrow-right</v-icon>
                    </v-btn>
                    <v-btn 
                        v-else
                        color="primary" 
                        depressed 
                        small
                        @click="finishTour"
                    >
                        Finish
                        <v-icon right small>mdi-check</v-icon>
                    </v-btn>
                </div>

                <!-- Progress dots -->
                <div class="tooltip-progress">
                    <span 
                        v-for="(step, index) in steps" 
                        :key="index"
                        class="progress-dot"
                        :class="{ 'active': index === currentStepIndex, 'completed': index < currentStepIndex }"
                    ></span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import WelcomeTourModal from './welcome-tour-modal.vue';

export default {
    name: 'OnboardingTour',
    components: {
        WelcomeTourModal
    },
    props: {
        user: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            showWelcomeModal: false,
            tourStarted: false,
            currentStepIndex: 0,
            currentHighlight: null,
            highlightStyle: {},
            tooltipStyle: {},
            tooltipPosition: 'bottom',
            resizeObserver: null,
            steps: [
                {
                    target: '[data-tour="side-menu"]',
                    title: 'Side Menu',
                    description: 'This is your main navigation. Access all features of Riwaq Al-Quran from here including your courses, calendar, wallet, and more.',
                    position: 'right',
                    padding: 0,
                    highlightFullElement: true
                },
                {
                    target: '[data-tour="courses-menu"]',
                    title: 'Course Catalog',
                    description: 'Browse our comprehensive collection of Quran courses. Find the perfect course for your learning journey.',
                    position: 'right',
                    padding: 4,
                    requiresMenuOpen: true // Keep menu open for this step
                },
                {
                    target: '[data-tour="search-filter"]',
                    title: 'Search & Filter',
                    description: 'Use the search and filter options to quickly find courses that match your interests and schedule.',
                    position: 'bottom',
                    padding: 8,
                    route: 'courses',
                    scrollTo: 'top'
                },
                {
                    target: '[data-tour="advisor-button"]',
                    title: 'Talk to an Advisor',
                    description: 'Need personalized guidance? Click here to connect with one of our education advisors who can help you choose the right course.',
                    position: 'left',
                    padding: 8,
                },
                {
                    target: '.zsiq-float',
                    title: 'Need Help?',
                    description: 'Click here anytime to chat with our support team. We\'re here to help you succeed!',
                    position: 'top-right',
                    padding: 8,
                    scrollTo: 'bottom'
                },
                {
                    target: '[data-tour="profile-menu"]',
                    title: 'Profile & Settings',
                    description: 'Access your profile, settings, and manage your account from this menu.',
                    position: 'bottom-right',
                    padding: 4,
                    scrollTo: 'top',
                }
            ]
        };
    },
    computed: {
        showTour() {
            return this.user && !this.user.tour_taken && this.user.email_verified_at && this.user.interests_completed == 1 && this.$route.name !== 'interests';
        },
        userName() {
            if (!this.user || !this.user.name) return '';
            return this.user.name.split(' ')[0];
        },
        currentStep() {
            return this.steps[this.currentStepIndex] || null;
        }
    },
    watch: {
        showTour: {
            immediate: true,
            handler(show) {
                if (show) {
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.showWelcomeModal = true;
                        }, 500);
                    });
                }
            }
        },
        currentStepIndex() {
            this.updateHighlight();
        },
        '$route'() {
            if (this.tourStarted) {
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.updateHighlight();
                    }, 300);
                });
            }
        }
    },
    methods: {
        startTour() {
            this.showWelcomeModal = false;
            this.tourStarted = true;
            this.currentStepIndex = 0;
            
            // Navigate to home first if not there
            if (this.$route.name !== 'home') {
                this.$router.push({ name: 'home' });
            }
            
            this.$nextTick(() => {
                // On mobile, open the side menu by clicking the menu button
                this.openSideMenuOnMobile();
                
                setTimeout(() => {
                    this.updateHighlight();
                }, 300);
            });
        },
        openSideMenuOnMobile() {
            // Check if we're on mobile (screen width < 960px is typical breakpoint)
            const isMobile = window.innerWidth < 960;
            
            if (isMobile) {
                // Try to access parent component's appMenu directly
                let appComponent = this.$parent;
                while (appComponent && appComponent.$options.name !== 'App') {
                    appComponent = appComponent.$parent;
                }
                
                if (appComponent && appComponent.appMenu !== undefined) {
                    appComponent.appMenu = true;
                    return;
                }
                
                // Fallback: Wait a bit for DOM to be ready then click button
                setTimeout(() => {
                    this.ensureMenuOpen();
                }, 200);
            }
        },
        ensureMenuOpen() {
            // Check if we're on mobile
            const isMobile = window.innerWidth < 960;
            
            if (isMobile) {
                // First, try to access parent component's appMenu directly
                let appComponent = this.$parent;
                while (appComponent && appComponent.$options.name !== 'App') {
                    appComponent = appComponent.$parent;
                }
                
                if (appComponent && appComponent.appMenu !== undefined) {
                    appComponent.appMenu = true;
                    return;
                }
                
                // Fallback: Check if menu is already open
                const drawer = document.querySelector('.v-navigation-drawer--active, .v-navigation-drawer--temporary');
                
                if (!drawer) {
                    // Try to find the menu button - look for button with mdi-menu icon that's visible
                    const findMenuButton = () => {
                        // Method 1: Find by icon class directly in navbar
                        const menuIcons = document.querySelectorAll('.mdi-menu');
                        for (let icon of menuIcons) {
                            const btn = icon.closest('.v-btn');
                            if (btn) {
                                // Check if button is in navbar and visible
                                const navbar = btn.closest('.navbar, .v-app-bar, .v-toolbar');
                                if (navbar && btn.offsetParent !== null) {
                                    return btn;
                                }
                            }
                        }
                        
                        // Method 2: Search all buttons in navbar
                        const navbars = document.querySelectorAll('.navbar, .v-app-bar, .v-toolbar');
                        for (let navbar of navbars) {
                            const buttons = navbar.querySelectorAll('.v-btn');
                            for (let btn of buttons) {
                                const icon = btn.querySelector('.mdi-menu');
                                if (icon && btn.offsetParent !== null) {
                                    return btn;
                                }
                            }
                        }
                        
                        return null;
                    };
                    
                    const menuButton = findMenuButton();
                    
                    if (menuButton) {
                        // Try clicking the button
                        try {
                            menuButton.click();
                        } catch (e) {
                            console.error('Error clicking menu button:', e);
                        }
                    } else {
                        // If button not found, try again after a short delay
                        setTimeout(() => {
                            const retryButton = findMenuButton();
                            if (retryButton) {
                                retryButton.click();
                            }
                        }, 200);
                    }
                }
            }
        },
        skipTour() {
            this.showWelcomeModal = false;
            this.completeTourOnServer();
        },
        endTour() {
            this.tourStarted = false;
            this.currentHighlight = null;
            this.completeTourOnServer();
        },
        finishTour() {
            this.tourStarted = false;
            this.currentHighlight = null;
            this.completeTourOnServer();
            
            // Show success message
            this.$emit('tour-completed');
        },
        async completeTourOnServer() {
            try {
                await this.$store.dispatch('completeTour');
            } catch (error) {
                console.error('Failed to save tour completion:', error);
            }
        },
        async nextStep() {
            const nextStep = this.steps[this.currentStepIndex + 1];
            
            // On mobile, ensure menu is open if next step requires it
            if (nextStep && nextStep.requiresMenuOpen) {
                this.ensureMenuOpen();
                // Wait for menu to open before proceeding
                await new Promise(resolve => setTimeout(resolve, 400));
            }
            
            // Check if we need to navigate to a different route
            if (nextStep && nextStep.route && this.$route.name !== nextStep.route) {
                this.$router.push({ name: nextStep.route });
                // Wait for route change then update step
                setTimeout(() => {
                    this.currentStepIndex++;
                }, 400);
            } else {
                // Handle scrolling before changing step if needed
                if (nextStep && nextStep.scrollTo) {
                    await this.scrollToPosition(nextStep.scrollTo);
                }
                this.currentStepIndex++;
            }
        },
        async prevStep() {
            const prevStep = this.steps[this.currentStepIndex - 1];
            
            // Check if we need to navigate back
            if (prevStep && this.currentStep.route && !prevStep.route) {
                this.$router.push({ name: 'home' });
                setTimeout(() => {
                    this.currentStepIndex--;
                }, 400);
            } else {
                // Handle scrolling before changing step if needed
                if (prevStep && prevStep.scrollTo) {
                    await this.scrollToPosition(prevStep.scrollTo);
                }
                this.currentStepIndex--;
            }
        },
        scrollToPosition(position) {
            return new Promise((resolve) => {
                if (position === 'bottom') {
                    window.scrollTo({
                        top: document.body.scrollHeight,
                        behavior: 'smooth'
                    });
                } else if (position === 'top') {
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    });
                }
                // Wait for scroll to complete
                setTimeout(resolve, 500);
            });
        },
        async updateHighlight() {
            const step = this.currentStep;
            if (!step) return;

            // Handle scrolling first if needed
            if (step.scrollTo) {
                await this.scrollToPosition(step.scrollTo);
            }

            const element = document.querySelector(step.target);
            
            if (element) {
                const rect = element.getBoundingClientRect();
                const padding = step.padding || 8;
                
                this.currentHighlight = rect;
                this.highlightStyle = {
                    top: `${rect.top - padding}px`,
                    left: `${rect.left - padding}px`,
                    width: `${rect.width + padding * 2}px`,
                    height: `${rect.height + padding * 2}px`
                };

                this.calculateTooltipPosition(rect, step.position);
            } else {
                // Element not found, try again after a short delay
                setTimeout(() => {
                    this.updateHighlight();
                }, 200);
            }
        },
        calculateTooltipPosition(targetRect, preferredPosition) {
            const tooltipWidth = 320;
            const tooltipHeight = 200;
            const margin = 16;
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            let top, left;
            this.tooltipPosition = preferredPosition || 'bottom';

            switch (this.tooltipPosition) {
                case 'bottom':
                    top = targetRect.bottom + margin;
                    left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
                    break;
                case 'top':
                    top = targetRect.top - tooltipHeight - margin;
                    left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
                    break;
                case 'right':
                    top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
                    left = targetRect.right + margin;
                    break;
                case 'left':
                    top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
                    left = targetRect.left - tooltipWidth - margin;
                    break;
                case 'bottom-right':
                    top = targetRect.bottom + margin;
                    left = targetRect.right - tooltipWidth;
                    break;
                case 'top-left':
                    top = targetRect.top - tooltipHeight - margin;
                    left = targetRect.left;
                    break;
                case 'top-right':
                    top = targetRect.top - tooltipHeight - margin;
                    left = targetRect.right - tooltipWidth;
                    break;
                default:
                    top = targetRect.bottom + margin;
                    left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
            }

            // Ensure tooltip stays within viewport
            if (left < margin) left = margin;
            if (left + tooltipWidth > viewportWidth - margin) {
                left = viewportWidth - tooltipWidth - margin;
            }
            if (top < margin) {
                top = margin;
            }
            if (top + tooltipHeight > viewportHeight - margin) {
                top = viewportHeight - tooltipHeight - margin;
            }

            this.tooltipStyle = {
                top: `${top}px`,
                left: `${left}px`,
                width: `${tooltipWidth}px`
            };
        },
        handleOverlayClick() {
            // Do nothing - require button clicks to navigate
        },
        handleResize() {
            if (this.tourStarted) {
                this.updateHighlight();
            }
        }
    },
    mounted() {
        window.addEventListener('resize', this.handleResize);
        window.addEventListener('scroll', this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('scroll', this.handleResize);
    }
};
</script>

<style lang="scss" scoped>
.onboarding-tour {
    position: relative;
    z-index: 9999;
}

.tour-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 9998;
}

.tour-highlight {
    position: fixed;
    background: transparent;
    border-radius: 8px;
    box-shadow: 
        0 0 0 4px rgba(231, 144, 7, 0.5),
        0 0 0 9999px rgba(0, 0, 0, 0.6);
    z-index: 9999;
    pointer-events: none;
    transition: all 0.3s ease;
}

.tour-tooltip {
    position: fixed;
    background: white;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    z-index: 10000;
    padding: 20px;
    animation: tooltipFadeIn 0.3s ease;
}

@keyframes tooltipFadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.tooltip-arrow {
    position: absolute;
    width: 0;
    height: 0;
    border: 10px solid transparent;

    &.arrow-bottom {
        top: -20px;
        left: 50%;
        transform: translateX(-50%);
        border-bottom-color: white;
    }

    &.arrow-top {
        bottom: -20px;
        left: 50%;
        transform: translateX(-50%);
        border-top-color: white;
    }

    &.arrow-right {
        left: -20px;
        top: 50%;
        transform: translateY(-50%);
        border-right-color: white;
    }

    &.arrow-left {
        right: -20px;
        top: 50%;
        transform: translateY(-50%);
        border-left-color: white;
    }

    &.arrow-bottom-right {
        top: -20px;
        right: 24px;
        border-bottom-color: white;
    }

    &.arrow-top-left {
        bottom: -20px;
        left: 24px;
        border-top-color: white;
    }

    &.arrow-top-right {
        bottom: -20px;
        right: 24px;
        border-top-color: white;
    }
}

.tooltip-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.step-indicator {
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: #E79007;
    background: rgba(231, 144, 7, 0.1);
    padding: 4px 10px;
    border-radius: 12px;
}

.close-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: background 0.2s;

    &:hover {
        background: rgba(0, 0, 0, 0.05);
    }
}

.tooltip-content {
    margin-bottom: 16px;
}

.tooltip-title {
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 16px;
    color: #1E1E1E;
    margin-bottom: 8px;
}

.tooltip-description {
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 14px;
    color: rgba(30, 30, 30, 0.7);
    line-height: 1.5;
    margin: 0;
}

.tooltip-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.tooltip-progress {
    display: flex;
    justify-content: center;
    gap: 6px;
}

.progress-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;

    &.active {
        background: #E79007;
        transform: scale(1.2);
    }

    &.completed {
        background: #994C26;
    }
}

// Mobile adjustments
@media (max-width: 600px) {
    .tour-tooltip {
        left: 16px !important;
        right: 16px;
        width: auto !important;
        max-width: calc(100vw - 32px);
    }

    .tooltip-arrow {
        display: none;
    }
}
</style>

